import styled, { keyframes } from "styled-components";

const previewAppear = keyframes`
    from {
        opacity: 0;
        transform: translateY(14px) scale(0.95);
    }

    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
`;

const previewPanelAppear = keyframes`
    from {
        opacity: 0;
        transform: translateY(-10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
`;

export const CardRoot = styled.div.attrs({
    role: "button",
    tabIndex: 0,
})`

    position: relative;
    width: 414px;
    min-width: 414px;
    max-width: 414px;
    border: 0;
    background: transparent;
    cursor: pointer;
    padding: 0;
    text-align: left;
    will-change: transform;
    transform: ${({ active }) => active ? "translateY(1px)" : "translateY(0)"};
    transition: transform 0.32s cubic-bezier(0.22, 1, 0.36, 1);
    z-index: ${({ active }) => active ? 5 : 1};

    &:focus-visible {
        outline-offset: 3px;
    }

    @media (max-width: 991px) {
        width: min(414px, 100%);
        min-width: min(414px, 100%);
        max-width: min(414px, 100%);
        height: auto;
        aspect-ratio: 414 / 258.75;
        transform: none;
        z-index: 1;
    }
`;

export const CardThumbnail = styled.div`
    box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;   
    position: relative;
    isolation: isolate;
    width: 100%;
    height: 232.875px;
    border-radius: 18px;
    overflow: hidden;
    transition:
        transform 0.32s cubic-bezier(0.22, 1, 0.36, 1),
        box-shadow 0.32s cubic-bezier(0.22, 1, 0.36, 1),
        border-color 0.32s cubic-bezier(0.22, 1, 0.36, 1),
        border-radius 0.32s cubic-bezier(0.22, 1, 0.36, 1);

    ${CardRoot}:hover & img {
        transform: scale(1.03);
    }


    @media (max-width: 991px) {
        height: auto;
        aspect-ratio: 16 / 9;
    }
`;

export const CardOverlay = styled.div`
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: inherit;
    background: linear-gradient(
        180deg,
        ${({ theme }) => theme.palette.colors.cardOverlayStart} 0%,
        ${({ active, theme }) => active ? theme.palette.colors.backgroundTransparent : theme.palette.colors.cardOverlayMiddle} 52%,
        ${({ active, theme }) => active ? theme.palette.colors.backgroundSoft : theme.palette.colors.cardOverlayEnd} 100%
    );
    transition: background 0.32s cubic-bezier(0.22, 1, 0.36, 1);
`;

export const CardBadge = styled.div`
    position: absolute;
    right: 12px;
    bottom: 12px;
    z-index: 2;
    min-height: 24px;
    border-radius: 8px;
    padding: 5px 10px;
    background: rgba(15, 23, 43, 0.84);
    color: ${({ theme }) => theme.palette.colors.text};
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
    letter-spacing: 0.02em;
    text-transform: uppercase;
`;

export const CardContent = styled.div`
    width: 100%;
    padding: 14px 2px 0;
    display: flex;
    align-items: flex-start;
    gap: 12px;
    opacity: ${({ active }) => active ? 0.08 : 1};
    transform: ${({ active }) => active ? "translateY(6px)" : "translateY(0)"};
    transition:
        opacity 0.24s ease,
        transform 0.24s ease;
`;

export const CardAvatar = styled.div`
    width: 42px;
    min-width: 42px;
    height: 42px;
    border-radius: 999px;
    background: linear-gradient(135deg, ${({ theme }) => theme.palette.blue.main} 0%, #0f4ad0 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 800;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.palette.colors.text};
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.18);
`;

export const CardTextBlock = styled.div`
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const CardMeta = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
`;

export const CardMetaLine = styled.div`
    font-size: 14px;
    line-height: 1.45;
    color: ${({ theme }) => theme.palette.colors.muted};
`;

export const CardMetaChip = styled.div`
    width: fit-content;
    min-height: 22px;
    border-radius: 999px;
    padding: 3px 8px;
    background: ${({ theme }) => theme.palette.colors.badgeMuted};
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
    color: ${({ theme }) => theme.palette.colors.text};
    text-transform: uppercase;
`;

export const CardTitle = styled.div`
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.28;
    color: ${({ theme }) => theme.palette.colors.text};

    @media (max-width: 991px) {
        font-size: 18px;
    }
`;
export const PreviewTitle = styled.div`
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.28;
    color: ${({ theme }) => theme.palette.secondary.main};

    @media (max-width: 991px) {
        font-size: 18px;
    }
`;

export const PreviewDescription = styled.div`
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme.palette.colors.text};
    opacity: 0.9;
`;

export const PreviewPanel = styled.div`
    position: relative;
    width: 100%;
    min-height: 128px;
    border-top: 0;
    border-radius: 0 0 18px 18px;
    padding: 16px 18px 18px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    opacity: ${({ active }) => active ? 1 : 0};
    pointer-events: none;
    transform: ${({ active }) => active ? "translateY(0) scale(1)" : "translateY(-16px) scale(0.98)"};
    z-index: 7;
    background: ${({ theme }) => theme.palette.primary.main};

    animation: ${previewPanelAppear} 0.28s cubic-bezier(0.22, 1, 0.36, 1) 0.05s both;
    @media (max-width: 991px) {
        display: none;
    }
`;

export const PreviewPortal = styled.div`
    position: fixed;
    z-index: 4000;
    pointer-events: none;
    
`;

export const PreviewFloatingCard = styled.div`
    width: 100%;
    border-radius: 18px;
    overflow: hidden;
    box-shadow:
        0 26px 46px ${({ theme }) => theme.palette.colors.shadowStrong},
        0 52px 96px ${({ theme }) => theme.palette.colors.shadowStrong};
    opacity: 0;
    animation: ${previewAppear} 0.26s cubic-bezier(0.22, 1, 0.36, 1) forwards;
    transform-origin: top center;
    will-change: transform, opacity;
    pointer-events: auto;
    cursor: pointer;
`;

export const PreviewFloatingMedia = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-bottom: 0;
    border-radius: 18px 18px 0px 0px;
    overflow: hidden;
`;

export const PreviewFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
`;

export const PreviewCategory = styled.div`
    max-width: 60%;
    min-height: 34px;
    border: 1px solid ${({ theme }) => theme.palette.colors.borderSoft};
    border-radius: 999px;
    padding: 7px 14px;
    background: ${({ theme }) => theme.palette.colors.badgeMuted};
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.14);
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
    color: ${({ theme }) => theme.palette.colors.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const PreviewPlayButton = styled.div`
    min-width: 116px;
    height: 48px;
    border-radius: 12px;
    padding: 0 18px;
    background: ${({ theme }) => theme.palette.colors.ctaBackground};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    box-shadow: ${({ theme }) => theme.palette.colors.ctaShadow};

    span {
        font-size: 16px;
        font-weight: 700;
        line-height: 1;
        color: ${({ theme }) => theme.palette.colors.ctaText};
    }
`;

export const PreviewPlayIcon = styled.img`
    width: 16px;
    height: 16px;
    filter: ${({ theme }) => theme.palette.colors.ctaText === "#FFFFFF" ? "none" : "invert(1)"};
`;
